import { useEffect, useState } from 'react';
import { useCan, useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { Alert, Box, Button, Collapse, Skeleton } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { isBatchRunning } from '../bulkVocabulary';
import {
  useGetBatchesQuery,
  useGetBatchPreviewQuery,
  useGetBatchQuery,
  useGetRequestCopyPreviewQuery,
} from '../store/api';
import { toBatchProgressData, toBulkLaunchPreviewData } from '../store/bulkLaunchMapping';
import { useBatchCancel } from '../store/useBatchCancel';
import { useBulkLaunch } from '../store/useBulkLaunch';

import { BatchProgressPanel } from './BatchProgressPanel';
import { BulkLaunchConfirmDialog } from './BulkLaunchConfirmDialog';
import { BulkLaunchPreview } from './BulkLaunchPreview';

/** How often a running batch re-reads its progress. */
const PROGRESS_POLL_MS = 5000;

export interface BulkLaunchRowProps {
  definitionId: string;
  requirementLabel: string;
  requirementKind?: string;
}

/**
 * Bulk launch from a requirement's catalog row (AC-10 … AC-14).
 *
 * **The preview is practice-admin only, and that is not a display rule.**
 * Verifying the population settles requirement rows org-wide and attributes
 * the read to the caller, so it is a write wearing a GET. A non-admin must
 * never CALL it — hiding the result afterwards would still have mutated state
 * on their behalf — which is why the query is skipped rather than the panel
 * hidden, and why the control that opens it is withheld rather than disabled.
 *
 * Launch, progress and cancel are not here: cut 2's endpoints do not exist
 * yet, and a control that cannot do its job is worse than an absent one.
 */
export const BulkLaunchRow = ({
  definitionId,
  requirementLabel,
  requirementKind,
}: BulkLaunchRowProps) => {
  const isAdmin = useCan('manage_organization');
  const organizationId = useOrganizationId();
  const subject = useSubjectLanguageVariants();
  const [open, setOpen] = useState(false);
  const launch = useBulkLaunch(organizationId, definitionId);
  const { patientRequirementsBulkLaunch } = useEventTracking();

  // Not merely hidden: skipped. See the note above.
  const canPreview = isAdmin && Boolean(organizationId);

  const {
    data: preview,
    isFetching,
    isError: previewFailed,
    refetch: refetchPreview,
  } = useGetBatchPreviewQuery(
    { orgId: organizationId ?? '', definitionId },
    {
      skip: !canPreview || !open,
      /**
       * Every open asks the server again, and that is not about freshness.
       *
       * The preview VERIFIES the population as it reads it — that is the AC-10
       * side effect, and it is attributed to the caller. Serving a reopen from
       * RTK Query's retained cache would show an audience that was verified
       * minutes ago and never re-verify it, so the admin would be reviewing a
       * claim the server is no longer making.
       */
      refetchOnMountOrArgChange: true,
    },
  );

  /**
   * The message is org-wide copy, not part of the batch preview — the audience
   * and the message are two different questions, answered by two endpoints.
   * Both are needed before a launch can be offered: AC-11 refuses to send
   * behind a message nobody has read.
   */
  const {
    data: copy,
    isFetching: copyIsFetching,
    isError: copyFailed,
    refetch: refetchCopy,
  } = useGetRequestCopyPreviewQuery(
    { orgId: organizationId ?? '' },
    { skip: !canPreview || !open },
  );

  /**
   * The batches this definition already has. Without it, reopening the row
   * after a launch shows a preview as though nothing had happened — and the
   * obvious next move for an admin looking at that is to launch again.
   */
  const {
    data: batches,
    isFetching: batchesIsFetching,
    isError: batchesFailed,
  } = useGetBatchesQuery(
    { orgId: organizationId ?? '', definitionId, limit: 1 },
    { skip: !canPreview || !open, refetchOnMountOrArgChange: true },
  );

  const activeBatchId = launch.launchedBatchId ?? batches?.items?.[0]?.batch_id;

  /**
   * Held in state rather than derived inline, because the interval has to be
   * decided before the query that reports the state has answered. It starts
   * true so a batch we have not read yet is polled once; the effect below
   * turns it off the moment the server says the batch has stopped.
   */
  const [progressIsRunning, setProgressIsRunning] = useState(true);

  const {
    data: batch,
    isError: batchFailed,
    refetch: refetchBatch,
  } = useGetBatchQuery(
    { orgId: organizationId ?? '', definitionId, batchId: activeBatchId ?? '' },
    {
      skip: !canPreview || !open || !activeBatchId,
      /**
       * Polls while the batch is working and stops when it is not. The
       * completed count keeps moving afterwards — the batch stops sending, the
       * patients do not stop completing — but a standing poll on a finished
       * batch would be a timer held open on a number the patients list reports
       * better. The panel says so instead, and re-reads when the row reopens.
       * PM ruling on AC-12.
       */
      pollingInterval: progressIsRunning ? PROGRESS_POLL_MS : 0,
      refetchOnMountOrArgChange: true,
    },
  );

  const cancelBatch = useBatchCancel(organizationId, definitionId, activeBatchId, () => {
    void refetchBatch();
  });

  useEffect(() => {
    if (!batch) return;
    // One definition of running, shared with the panel and the mapper.
    setProgressIsRunning(isBatchRunning(batch.state));
  }, [batch]);

  if (!isAdmin) return null;

  /**
   * The two failures are not the same failure, and collapsing them would lose
   * the more useful half.
   *
   * No audience means there is nothing to render at all — the panel is a
   * description of a population, so it needs a retry rather than a shape.
   * No COPY, with the audience in hand, is precisely what the panel's own
   * "the message could not be loaded" alert says, and it already withholds the
   * send behind it. Replacing that with a generic error would tell an admin
   * less than the panel would have.
   */
  const audienceFailed = previewFailed;

  /**
   * The two requests do not finish together, and an absent message reads on
   * the panel as a load failure. Rendering the moment the audience lands would
   * therefore show "the message could not be loaded" for the ordinary case of
   * one request being quicker than the other — so wait for copy, unless copy
   * is the thing that failed, in which case that alert is now true.
   */
  /**
   * Whether a batch already exists is part of "has this row finished loading",
   * not a detail that can settle afterwards (jotreviewer, #2967).
   *
   * The batch queries are two round trips behind the preview — `activeBatchId`
   * comes from the list, and the progress query cannot start until it does —
   * so on any open where preview and copy answer first, this component would
   * otherwise fall through to the preview branch and render a LIVE Send button
   * for a definition that is already being messaged. An admin clicking in that
   * window launches a second batch at the same population, which is the exact
   * failure the batch-replaces-preview rule exists to prevent.
   */
  const batchExistenceUnknown = !batchesFailed && (batchesIsFetching || batches === undefined);
  const activeBatchPending = Boolean(activeBatchId) && !batch && !batchFailed;

  /**
   * Whether a batch is already running is UNANSWERED if either query failed:
   * the list, or the progress read for an id the list did give us.
   *
   * Those two failures were handled in two places and answered differently
   * (jotreviewer, #2967 round 2) — `activeBatchPending` excuses `batchFailed`
   * from blocking, correctly, or a failed progress read would leave a
   * permanent skeleton; but the launch gate then never asked about it. So a
   * list that succeeded with a running batch, plus a progress read that
   * failed, left a live Send for a definition already being messaged. One
   * predicate now, used wherever the answer matters, because the asymmetry is
   * what produced the bug rather than either half being wrong.
   */
  const batchStateUnknown = batchesFailed || (Boolean(activeBatchId) && batchFailed);

  const stillLoading =
    isFetching ||
    copyIsFetching ||
    (Boolean(preview) && !copy && !copyFailed) ||
    batchExistenceUnknown ||
    activeBatchPending;

  const progress = batch ? toBatchProgressData(batch) : undefined;

  const data = preview
    ? toBulkLaunchPreviewData(
        preview,
        copy
          ? {
              smsBody: copy.sms.body,
              emailSubject: copy.email.subject,
              emailBody: copy.email.body,
            }
          : undefined,
      )
    : undefined;

  return (
    /*
      The button is the row's ACTION, so it sits at the row's right-hand edge
      (GDN-3537); the panel it opens is a full-width read, so it stretches back
      out. Only this component can make that distinction — from the outside the
      two are one node — which is why the alignment lives here rather than on
      the cell that holds it.
    */
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {!open && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setOpen(true);
            // Ids, kinds and counts only — never the practice-authored label
            // or the message copy, and no patient_id: an admin acting on 128
            // people at once is one interaction, not 128.
            patientRequirementsBulkLaunch.opened({
              organization_id: organizationId ?? undefined,
              patient_requirement_definition_id: definitionId,
              kind: requirementKind,
            });
          }}
          sx={{ textTransform: 'none' }}
        >
          Send requests…
        </Button>
      )}

      <Collapse in={open} unmountOnExit sx={{ alignSelf: 'stretch' }}>
        {audienceFailed ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void refetchPreview();
                  if (copyFailed) void refetchCopy();
                }}
              >
                Try again
              </Button>
            }
          >
            The preview could not be loaded, so there is no audience to review.
          </Alert>
        ) : stillLoading ? (
          <Skeleton variant="rounded" height={220} />
        ) : progress ? (
          <>
            {cancelBatch.errorMessage && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {cancelBatch.errorMessage}
              </Alert>
            )}
            {/*
              A batch exists, so the audience question is settled and the panel
              answers the next one instead. Showing the preview here would
              invite a second launch for a population already being messaged.
            */}
          <BatchProgressPanel
            requirementLabel={requirementLabel}
            state={progress.state}
            counts={progress.counts}
            completedPatients={progress.completedPatients}
            skippedPatients={progress.skippedPatients}
            skippedBreakdown={progress.skippedBreakdown}
            subject={subject}
            isCancelling={cancelBatch.isCancelling}
            onCancel={() => {
              // Counts at the moment of the click, which is what says how much
              // was stopped — after the cancel lands they describe the outcome
              // instead, and the difference is the whole point of the event.
              patientRequirementsBulkLaunch.stopClicked({
                organization_id: organizationId ?? undefined,
                patient_requirement_definition_id: definitionId,
                batch_id: progress.batchId,
                queued_count: progress.counts.queued,
                sent_count: progress.counts.sent,
              });
              cancelBatch.cancel();
            }}
            />
          </>
        ) : data ? (
          <BulkLaunchPreview
            requirementLabel={requirementLabel}
            requirementKind={requirementKind}
            eligiblePatients={data.eligiblePatients}
            alreadyCompletePatients={data.alreadyCompletePatients}
            excludedPatients={data.excludedPatients}
            messagesToSend={data.messagesToSend}
            exclusionBreakdown={data.exclusionBreakdown}
            excludedPatientsPage={data.excludedPatientsPage}
            messagePreview={data.messagePreview}
            canLaunch={data.canLaunch}
            subject={subject}
            /*
              Withheld whenever we could not find out whether a batch is
              already running. The audience is still worth reading, but
              offering a send on an unknown answer is the one thing here that
              cannot be taken back.
            */
            onLaunch={batchStateUnknown ? undefined : launch.openConfirm}
            onDismiss={() => {
              setOpen(false);
              patientRequirementsBulkLaunch.closed({
                organization_id: organizationId ?? undefined,
                patient_requirement_definition_id: definitionId,
                kind: requirementKind,
                eligible_count: data.eligiblePatients,
              });
            }}
          />
        ) : null}
      </Collapse>

      {data && (
        <BulkLaunchConfirmDialog
          open={launch.isConfirming}
          requirementLabel={requirementLabel}
          eligiblePatients={data.eligiblePatients}
          messagesToSend={data.messagesToSend}
          subject={subject}
          isLaunching={launch.isLaunching}
          errorMessage={launch.errorMessage}
          onConfirm={() => {
            // Both counts ride the submit on purpose: a launch of 128 that
            // produces 217 delivery rows reads as a discrepancy when the two
            // numbers come from different places.
            patientRequirementsBulkLaunch.submitted({
              organization_id: organizationId ?? undefined,
              patient_requirement_definition_id: definitionId,
              kind: requirementKind,
              eligible_count: data.eligiblePatients,
              message_count: data.messagesToSend,
            });
            launch.confirm();
          }}
          onCancel={launch.closeConfirm}
        />
      )}
    </Box>
  );
};
